import React, { useCallback } from 'react';
import styled from '@emotion/styled';
import type { SignupFlow } from 'corgi-types';
import type { WidthProps } from 'styled-system';
import { flex, width } from 'styled-system';

import type { FlexProps } from 'common/components/Flex';
import { Flex } from 'common/components/Flex';
import type { CheckboxFieldProps } from 'common/components/Form/CheckboxField';
import CheckboxField from 'common/components/Form/CheckboxField';
import { CheckboxValue, FieldChangeTrigger } from 'common/enums/form';
import { useKeyboardSelect } from 'common/hooks/keyboard';
import ForgotPasswordLink from './ForgotPasswordLink';

type CommonCheckboxProps = Pick<CheckboxFieldProps, 'name' | 'value' | 'onChange' | 'disabled'> & {
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
};

export interface RememberMeProps extends CommonCheckboxProps {
  userFlow: SignupFlow;
  labelText: string;
  withForgotPasswordLink?: boolean;
}

const StyledCheckbox = styled(CheckboxField)<WidthProps & FlexProps>`
  ${width};
  ${flex};
`;

const RememberMeCheckbox = ({
  userFlow,
  labelText,
  withForgotPasswordLink = true,
  ...checkboxProps
}: RememberMeProps) => {
  const handleChange = useCallback(() => {
    checkboxProps.onChange?.({
      fieldName: checkboxProps.name,
      value: checkboxProps.value === CheckboxValue.True ? CheckboxValue.False : CheckboxValue.True,
      trigger: FieldChangeTrigger.Change,
    });
  }, [checkboxProps]);
  const handleKeyDown = useKeyboardSelect(handleChange);

  return (
    <Flex
      alignItems="center"
      justifyContent="space-between"
      mb="sp_16"
      width="100%"
      onKeyDown={handleKeyDown}
    >
      <StyledCheckbox width="auto" flex="1 1 0" {...checkboxProps} label={labelText} />
      {withForgotPasswordLink && <ForgotPasswordLink userFlow={userFlow} />}
    </Flex>
  );
};

export default RememberMeCheckbox;
